import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studioProjects } from '@/lib/api';

interface Project {
  id: string;
  name: string;
  status: string;
  client_count: number;
  last_deployed_at?: string | null;
  deployment_id?: string | null;
}

export function MyApps() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await studioProjects.list();
        setProjects((data.projects ?? []) as Project[]);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  async function createProject() {
    setCreating(true);
    try {
      const result = await studioProjects.create({ name: 'Untitled App' });
      navigate(`/builder/${result.id}`);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Apps</h1>
          <p className="text-muted-foreground mt-1">Manage your client-facing apps</p>
        </div>
        <button
          onClick={() => void createProject()}
          disabled={creating}
          className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium"
        >
          {creating ? 'Creating…' : '+ New App'}
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground mb-4">No apps yet. Start from a template or blank canvas.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/starters')}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm"
            >
              Browse Starters
            </button>
            <button
              onClick={() => navigate('/ideas')}
              className="border border-border px-4 py-2 rounded-lg text-sm"
            >
              Explore Ideas
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="border border-border rounded-xl p-5 hover:border-primary/50 cursor-pointer transition-colors bg-card"
              onClick={() => navigate(`/apps/${project.id}`)}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-foreground truncate">{project.name}</h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                    project.status === 'deployed'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {project.status}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {project.client_count} client{project.client_count !== 1 ? 's' : ''}
              </p>
              {project.last_deployed_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Deployed {new Date(project.last_deployed_at).toLocaleDateString()}
                </p>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/builder/${project.id}`);
                }}
                className="mt-3 text-xs text-primary hover:underline"
              >
                Open Builder →
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
